/**
 * Complete the Set — live variant sync + bundle ATC + live summary totals.
 */
(function () {
  const EVENT_VARIANT_UPDATE = 'variant:update';
  const EVENT_VARIANT_SELECTED = 'variant:selected';

  function formatMoney(cents, format) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, format);
      }
    } catch (_) {
      /* fall through */
    }

    const amount = (Number(cents) / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const template = format || '${{amount}}';
    return template
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  class CompleteTheSetDynamic extends HTMLElement {
    connectedCallback() {
      if (this._ctsBound) return;
      this._ctsBound = true;

      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currency = this.dataset.currency || 'USD';
      this.productId = String(this.dataset.productId || '');

      // Event delegation — always works, even if the node list changes
      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.classList.contains('cts-item__checkbox')) {
          this.updateSummary();
        }
      });

      // Select-all: handled solely by button onclick → window.__ctsToggleSelectAll
      // (document/CE listeners double-flipped the checkboxes and made it look broken).

      this.addEventListener('click', (event) => {
        const target =
          event.target instanceof Element ? event.target : event.target && event.target.parentElement;
        if (!(target instanceof Element)) return;

        if (target.closest('.cts-select-all') || target.closest('[data-cts-select-all]')) {
          return;
        }

        if (target.closest('.cts-summary__button')) {
          event.preventDefault();
          this.addBundleToCart();
          return;
        }

        // Label clicks land on thumb/title; recompute total after native toggle
        if (target.closest('.cts-item') && !target.closest('[data-anchor]')) {
          requestAnimationFrame(() => this.updateSummary());
        }
      });

      document.addEventListener(EVENT_VARIANT_UPDATE, this.onVariantUpdate, true);
      document.addEventListener(EVENT_VARIANT_SELECTED, this.onVariantSelected, true);
      document.addEventListener('change', this.onPickerChange, true);
      document.addEventListener('click', this.onPickerClick, true);

      this.watchProductForm();

      // Initial total (and again after paint in case DOM settles late)
      this.updateSummary();
      requestAnimationFrame(() => this.updateSummary());
      this.syncFromPage();
    }

    disconnectedCallback() {
      document.removeEventListener(EVENT_VARIANT_UPDATE, this.onVariantUpdate, true);
      document.removeEventListener(EVENT_VARIANT_SELECTED, this.onVariantSelected, true);
      document.removeEventListener('change', this.onPickerChange, true);
      document.removeEventListener('click', this.onPickerClick, true);
      if (this._formObserver) {
        this._formObserver.disconnect();
        this._formObserver = null;
      }
      this._ctsBound = false;
    }

    getCheckboxes() {
      return Array.from(this.querySelectorAll('input.cts-item__checkbox'));
    }

    getVariantsScript(item) {
      const wrap = item.closest('.cts-item-wrap');
      if (wrap) {
        const script = wrap.querySelector('script.cts-item__variants');
        if (script) return script;
      }
      // Back-compat (older markup)
      return item.querySelector('script.cts-item__variants');
    }

    watchProductForm() {
      const form =
        document.querySelector(`product-form-component[data-product-id="${this.productId}"]`) ||
        document.querySelector(
          'product-form-component:not(product-card product-form-component):not(quick-add-dialog product-form-component)'
        );
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!(idInput instanceof HTMLInputElement)) return;

      idInput.addEventListener('change', () => this.syncFromPage());
      idInput.addEventListener('input', () => this.syncFromPage());

      this._formObserver = new MutationObserver(() => this.syncFromPage());
      this._formObserver.observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    getVariantPicker() {
      return (
        document.querySelector(`variant-picker[data-product-id="${this.productId}"]`) ||
        document.querySelector(
          'variant-picker[data-template-product-match="true"]:not(product-card variant-picker):not(quick-add-dialog variant-picker)'
        )
      );
    }

    onPickerChange = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const picker = this.getVariantPicker();
      if (!picker || !picker.contains(target)) return;
      queueMicrotask(() => this.syncFromPage());
    };

    onPickerClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const picker = this.getVariantPicker();
      if (!picker || !picker.contains(target)) return;
      setTimeout(() => this.syncFromPage(), 0);
      setTimeout(() => this.syncFromPage(), 150);
    };

    onVariantSelected = () => {
      this.syncFromPage();
    };

    onVariantUpdate = (event) => {
      const detail = event.detail || {};
      const resource = detail.resource;
      const data = detail.data || {};
      if (data.productId != null && String(data.productId) !== this.productId) return;

      if (resource) {
        this.applyAnchorVariant(resource);
        this.syncLinkedItemsColor(this.colorFromVariant(resource));
        return;
      }
      this.syncFromPage();
    };

    syncFromPage() {
      const resource = this.readVariantJson();
      if (resource) {
        this.applyAnchorVariant(resource);
        this.syncLinkedItemsColor(this.colorFromVariant(resource));
        return;
      }
      const color = this.readSelectedColor();
      if (color) this.syncLinkedItemsColor(color);
    }

    readVariantJson() {
      const picker = this.getVariantPicker();
      const text = picker?.querySelector('script[type="application/json"]')?.textContent;
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    }

    readSelectedColor() {
      const picker = this.getVariantPicker();
      if (!picker) return null;
      const checked = picker.querySelector(
        'fieldset:first-of-type input:checked, fieldset:first-of-type select option:checked'
      );
      if (checked instanceof HTMLInputElement || checked instanceof HTMLOptionElement) {
        const value = (checked.value || '').trim();
        if (value) return value;
      }
      return picker.querySelector('.variant-option__swatch-value')?.textContent?.trim() || null;
    }

    colorFromVariant(resource) {
      if (!resource) return null;
      if (resource.option1) return String(resource.option1);
      if (Array.isArray(resource.options) && resource.options[0]) return String(resource.options[0]);
      return null;
    }

    applyAnchorVariant(resource) {
      const anchorItem = this.querySelector('.cts-item[data-anchor="true"]');
      if (!anchorItem || !resource) return;

      anchorItem.dataset.variantId = String(resource.id);
      anchorItem.dataset.price = String(resource.price);
      anchorItem.dataset.available = String(!!resource.available);

      const priceEl = anchorItem.querySelector('.cts-item__price');
      if (priceEl) priceEl.textContent = formatMoney(resource.price, this.moneyFormat);

      const image =
        resource.featured_media?.preview_image?.src ||
        resource.featured_image?.src ||
        (typeof resource.featured_image === 'string' ? resource.featured_image : null);

      const imgEl = anchorItem.querySelector('.cts-item__img');
      if (image && imgEl instanceof HTMLImageElement) {
        let url = image.startsWith('//') ? `https:${image}` : image;
        if (!url.includes('width=')) url += (url.includes('?') ? '&' : '?') + 'width=200';
        if (imgEl.src !== url) {
          imgEl.src = url;
          imgEl.srcset = '';
        }
      }
    }

    syncLinkedItemsColor(color) {
      if (!color) return;
      const colorLower = color.toLowerCase();

      this.querySelectorAll('.cts-item:not([data-anchor])').forEach((item) => {
        const variantsScript = this.getVariantsScript(item);
        if (!(variantsScript instanceof HTMLScriptElement)) return;

        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent || '[]');
        } catch {
          return;
        }

        const target =
          variants.find((v) => String(v.color || '').toLowerCase() === colorLower && v.available) ||
          variants.find((v) => String(v.color || '').toLowerCase() === colorLower) ||
          variants.find((v) => v.available) ||
          variants[0];

        if (!target) return;

        const wasAvailable = item.getAttribute('data-available') === 'true';
        const isAvailable = Boolean(target.available);

        item.dataset.variantId = String(target.id);
        item.dataset.price = String(target.price);
        item.dataset.available = String(isAvailable);
        item.dataset.color = target.color;
        item.classList.toggle('cts-item--sold-out', !isAvailable);

        const checkbox = item.querySelector('.cts-item__checkbox');
        if (checkbox instanceof HTMLInputElement) {
          checkbox.disabled = !isAvailable;
          if (wasAvailable !== isAvailable) checkbox.checked = isAvailable;
        }

        const priceEl = item.querySelector('.cts-item__price');
        if (priceEl) {
          priceEl.textContent = isAvailable ? formatMoney(target.price, this.moneyFormat) : 'Sold out';
        }

        const imgEl = item.querySelector('.cts-item__img');
        if (imgEl instanceof HTMLImageElement && target.image) {
          let url = String(target.image);
          if (url.startsWith('//')) url = `https:${url}`;
          if (imgEl.src !== url) {
            imgEl.src = url;
            imgEl.srcset = '';
          }
        }
      });

      this.updateSummary();
    }

    updateSummary() {
      const checkboxes = this.getCheckboxes();
      let total = 0;
      let count = 0;

      checkboxes.forEach((checkbox) => {
        if (!(checkbox instanceof HTMLInputElement) || !checkbox.checked) return;
        const item = checkbox.closest('.cts-item');
        total += Number(item?.dataset.price || 0);
        count += 1;
      });

      // Re-query every time (avoid stale / missing refs after HTML morphs)
      const totalEl = this.querySelector('.cts-summary__total');
      const countEl = this.querySelector('.cts-summary__count');
      const button = this.querySelector('.cts-summary__button');
      const selectAllButton = this.querySelector('.cts-select-all');

      if (countEl) countEl.textContent = String(count);
      if (totalEl) totalEl.textContent = formatMoney(total, this.moneyFormat);
      if (button) button.disabled = count === 0;

      if (selectAllButton) {
        const toggleable = checkboxes.filter((c) => c instanceof HTMLInputElement && !c.disabled);
        if (toggleable.length > 0) {
          const allSelected = toggleable.every((c) => c.checked);
          selectAllButton.textContent = allSelected ? 'Deselect all' : 'Select all';
        }
      }
    }

    toggleSelectAll() {
      const toggleable = this.getCheckboxes().filter(
        (c) => c instanceof HTMLInputElement && !c.disabled
      );
      if (!toggleable.length) return;
      const shouldSelectAll = !toggleable.every((c) => c.checked);
      toggleable.forEach((c) => {
        c.checked = shouldSelectAll;
      });
      this.updateSummary();
    }

    async addBundleToCart() {
      const button = this.querySelector('.cts-summary__button');
      const errorEl = this.querySelector('.cts-summary__error');
      if (!button) return;

      const items = this.getCheckboxes()
        .filter((c) => c instanceof HTMLInputElement && c.checked)
        .map((c) => Number(c.closest('.cts-item')?.dataset.variantId))
        .filter((id) => Boolean(id))
        .map((id) => ({ id, quantity: 1 }));

      if (items.length === 0) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = this.dataset.addingText || 'Adding…';
      errorEl?.classList.add('hidden');

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((element) => {
        if (element instanceof HTMLElement && element.dataset.sectionId) {
          sectionIds.push(element.dataset.sectionId);
        }
      });

      const cartAddUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
        (window.routes && window.routes.cart_add_url) ||
        '/cart/add.js';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        });
        const result = await response.json();

        if (result.status) {
          if (errorEl) {
            errorEl.textContent = result.message || this.dataset.errorText || 'Error';
            errorEl.classList.remove('hidden');
          }
          button.textContent = originalLabel;
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: this.id,
              data: {
                source: 'complete-the-set',
                itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
                productId: this.productId,
                sections: result.sections,
              },
            },
          })
        );

        button.textContent = this.dataset.addedText || 'Added!';
        setTimeout(() => {
          button.textContent = originalLabel;
        }, 2000);
      } catch (error) {
        console.error(error);
        if (errorEl) {
          errorEl.textContent = this.dataset.errorText || 'Error';
          errorEl.classList.remove('hidden');
        }
        button.textContent = originalLabel;
      } finally {
        button.disabled = false;
      }
    }
  }

  if (!customElements.get('complete-the-set-dynamic')) {
    customElements.define('complete-the-set-dynamic', CompleteTheSetDynamic);
  }
  // Older cached pages used this tag name
  if (!customElements.get('complete-the-set-bundle')) {
    customElements.define('complete-the-set-bundle', CompleteTheSetDynamic);
  }

  /**
   * Global fallback: if summary nodes sit outside the custom element
   * (broken label/script nesting) or CE never upgrades, still keep totals live.
   */
  function updateCtsSummary(root) {
    if (!root) return;
    const boxes = root.querySelectorAll('input.cts-item__checkbox');
    let total = 0;
    let count = 0;
    boxes.forEach((c) => {
      if (!(c instanceof HTMLInputElement) || !c.checked) return;
      const item = c.closest('.cts-item');
      total += Number(item?.dataset.price || 0);
      count += 1;
    });
    const countEl = root.querySelector('.cts-summary__count');
    const totalEl = root.querySelector('.cts-summary__total');
    const button = root.querySelector('.cts-summary__button');
    const selectAll = root.querySelector('.cts-select-all');
    if (countEl) countEl.textContent = String(count);
    if (totalEl) totalEl.textContent = formatMoney(total, root.dataset?.moneyFormat || '${{amount}}');
    if (button) button.disabled = count === 0;
    if (selectAll) {
      const toggleable = Array.from(boxes).filter((c) => c instanceof HTMLInputElement && !c.disabled);
      if (toggleable.length) {
        selectAll.textContent = toggleable.every((c) => c.checked) ? 'Deselect all' : 'Select all';
      }
    }
  }

  function findCtsRoot(from) {
    // Prefer the shopify section so summary still updates if HTML nesting broke
    // (legacy markup put a <script> inside <label>, which can pull nodes out of the CE).
    return (
      from.closest('.shopify-section') ||
      from.closest('complete-the-set-dynamic') ||
      from.closest('complete-the-set-bundle') ||
      document.querySelector('#shopify-section-template--20334419116223__complete_the_set') ||
      document.querySelector('[id*="complete_the_set"]')
    );
  }

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.classList.contains('cts-item__checkbox')) return;
      updateCtsSummary(findCtsRoot(target));
    },
    true
  );

  function eventTargetEl(event) {
    return event.target instanceof Element
      ? event.target
      : event.target && event.target.parentElement;
  }

  /**
   * Document-level:
   * - Item clicks → refresh summary
   * - Select all → only if button has no dedicated onclick (legacy markup)
   */
  function handleSelectAllClick(event) {
    const target = eventTargetEl(event);
    if (!(target instanceof Element)) return;

    const selectBtn = target.closest('.cts-select-all, [data-cts-select-all]');
    if (selectBtn) {
      // Live markup uses onclick=window.__ctsToggleSelectAll — do NOT toggle here
      // or the same click will flip twice and appear broken.
      if (
        selectBtn.getAttribute('onclick') ||
        selectBtn.hasAttribute('data-cts-select-all') ||
        typeof window.__ctsToggleSelectAll === 'function'
      ) {
        return;
      }

      event.preventDefault();
      const host = selectBtn.closest('complete-the-set-dynamic, complete-the-set-bundle');
      if (host && typeof host.toggleSelectAll === 'function') {
        host.toggleSelectAll();
        return;
      }
      const root = findCtsRoot(selectBtn);
      if (!root) return;
      const boxes = Array.from(root.querySelectorAll('input.cts-item__checkbox:not([disabled])'));
      if (!boxes.length) return;
      const shouldSelectAll = !boxes.every((c) => c.checked);
      boxes.forEach((c) => {
        c.checked = shouldSelectAll;
      });
      if (host && typeof host.updateSummary === 'function') {
        host.updateSummary();
      } else {
        updateCtsSummary(root);
      }
      return;
    }

    if (target.closest('.cts-item')) {
      const root = findCtsRoot(target);
      requestAnimationFrame(() => updateCtsSummary(root));
      setTimeout(() => updateCtsSummary(root), 0);
    }
  }

  document.addEventListener('click', handleSelectAllClick, true);

  // Initial pass after load (covers "all selected" default total)
  function bootSummary() {
    document.querySelectorAll('[id*="complete_the_set"], complete-the-set-dynamic, complete-the-set-bundle').forEach((root) => {
      updateCtsSummary(root.closest('.shopify-section') || root);
    });
    // Also any section that has the strip test id
    document.querySelectorAll('[data-testid="complete-the-set-strip"]').forEach((strip) => {
      updateCtsSummary(strip.closest('.shopify-section') || strip.parentElement);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootSummary);
  } else {
    bootSummary();
  }
  setTimeout(bootSummary, 50);
  setTimeout(bootSummary, 300);
})();
